import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { CalendarDays, Building2, Link2 } from "lucide-react";
import PatientsTab from "./PatientsTab";
import ServicesTab from "./ServicesTab";
import ProvidersTab from "./ProvidersTab";
import AppointmentsTab from "./AppointmentsTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Booking is scoped per clinic (a med-spa client of the agency), not
 * global - every /api/v1/booking route takes a client_id. This picks
 * the clinic first, using the existing generic GET /api/clients route
 * (already scoped_query'd, so a Sales Rep or CSM only sees the clinics
 * they're allowed to), then hands that id down to the four tabs.
 */
const BookingCenter = ({ user }) => {
  const [clinics, setClinics] = useState([]);
  const [loadingClinics, setLoadingClinics] = useState(true);
  const [noClinicAccess, setNoClinicAccess] = useState(false);
  const [clinicId, setClinicId] = useState("");
  const [activeTab, setActiveTab] = useState("appointments");
  const [togglingPublicBooking, setTogglingPublicBooking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/clients`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (res.status === 403) {
          // Booking is per-clinic data; roles without the Clients module
          // (e.g. Sales Rep / Sales Manager) can't list or open clinics.
          if (!cancelled) setNoClinicAccess(true);
          return;
        }
        if (!res.ok) throw new Error(`Failed to load clinics (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : data.records || [];
        setClinics(rows);
        if (rows.length === 1) setClinicId(rows[0].id);
      } catch (err) {
        if (!cancelled) toast.error(err.message || "Failed to load clinics");
      } finally {
        if (!cancelled) setLoadingClinics(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.token]);

  const currentClinic = clinics.find((c) => c.id === clinicId);
  const publicBookingEnabled = currentClinic?.public_booking_enabled === true;
  const publicBookingLink = clinicId ? `${window.location.origin}/book/${clinicId}` : "";

  const togglePublicBooking = async (enabled) => {
    setTogglingPublicBooking(true);
    try {
      const res = await fetch(`${API}/clients/${clinicId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${user.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ data: { public_booking_enabled: enabled } }),
      });
      if (!res.ok) throw new Error(`Failed to update (${res.status})`);
      const updated = await res.json();
      setClinics((prev) =>
        prev.map((c) => (c.id === clinicId ? { ...c, public_booking_enabled: updated.public_booking_enabled } : c))
      );
      toast.success(enabled ? "Public booking enabled" : "Public booking disabled");
    } catch (err) {
      toast.error(err.message || "Failed to update public booking setting");
    } finally {
      setTogglingPublicBooking(false);
    }
  };

  const copyPublicBookingLink = async () => {
    try {
      await navigator.clipboard.writeText(publicBookingLink);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy - copy the link manually");
    }
  };

  return (
    <div className="page-stack" data-testid="booking-center">
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Booking</p>
          <h2>Patients, Providers &amp; Scheduling</h2>
          <p className="login-copy">Real conflict-aware scheduling per clinic - patients, treatment menu, providers, and appointments.</p>
        </div>
      </section>

      <Card className="glass-panel border-white/10">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <Building2 className="h-4 w-4 text-white/50" />
            <span className="text-sm text-white/70">Clinic</span>
            <div className="w-72">
              <Select value={clinicId} onValueChange={setClinicId} disabled={loadingClinics || clinics.length === 0}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="clinic-select">
                  <SelectValue placeholder={loadingClinics ? "Loading clinics..." : "Select a clinic"} />
                </SelectTrigger>
                <SelectContent>
                  {clinics.map((clinic) => (
                    <SelectItem key={clinic.id} value={clinic.id}>{clinic.clinic_name || clinic.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {clinicId && (
        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <Link2 className="h-4 w-4 text-white/50" />
                <div>
                  <p className="text-sm text-white/80">Public booking link</p>
                  <p className="text-xs text-white/50">Let patients book themselves online, no login required.</p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <Switch
                  checked={publicBookingEnabled}
                  disabled={togglingPublicBooking}
                  onCheckedChange={togglePublicBooking}
                  data-testid="public-booking-toggle"
                />
                {publicBookingEnabled ? "Enabled" : "Disabled"}
              </label>
            </div>
            {publicBookingEnabled && (
              <div className="mt-3 flex items-center gap-2">
                <code className="text-xs text-white/70 bg-white/5 border border-white/10 rounded px-2 py-1 flex-1 overflow-x-auto whitespace-nowrap">
                  {publicBookingLink}
                </code>
                <Button className="ghost-btn text-xs" onClick={copyPublicBookingLink} data-testid="copy-public-booking-link">
                  Copy
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!loadingClinics && noClinicAccess && (
        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6 text-center py-12" data-testid="booking-no-clinic-access">
            <CalendarDays className="h-10 w-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">Your role doesn't have access to clinics, so clinic booking isn't available. Ask a Master Admin to grant the Clients module if you need it.</p>
          </CardContent>
        </Card>
      )}

      {!loadingClinics && !noClinicAccess && clinics.length === 0 && (
        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6 text-center py-12">
            <CalendarDays className="h-10 w-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No clinics yet. Convert a lead into a client first, then come back here to set up booking.</p>
          </CardContent>
        </Card>
      )}

      {clinicId && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="glass-panel flex-wrap">
            <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
            <TabsTrigger value="patients" data-testid="tab-patients">Patients</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
            <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          </TabsList>
          <TabsContent value="appointments">
            <AppointmentsTab basePath={`/api/v1/booking/clinics/${clinicId}`} token={user.token} />
          </TabsContent>
          <TabsContent value="patients">
            <PatientsTab basePath={`/api/v1/booking/clinics/${clinicId}`} token={user.token} />
          </TabsContent>
          <TabsContent value="services">
            <ServicesTab basePath={`/api/v1/booking/clinics/${clinicId}`} token={user.token} />
          </TabsContent>
          <TabsContent value="providers">
            <ProvidersTab basePath={`/api/v1/booking/clinics/${clinicId}`} token={user.token} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default BookingCenter;
